import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { ApiService } from '../../shared/services';
import * as AssistantActions from './assistant.actions';
import * as CrmActions from '../../store/crm/crm.actions';
import { selectCurrentUser } from '../../store/auth/auth.selectors';
import { selectCurrentMatter } from '../../store/crm/crm.selectors';
import { selectAssistantMessages, selectCurrentConversationId, selectPendingWrite, selectAdminSessionId, selectAdminSessionState, selectAdminHistory, selectPendingClarification } from './assistant.selectors';
import { AssistantMessage } from '../../shared/models/assistant.model';

function messagesToHistory(messages: AssistantMessage[]): Array<{ role: string; content: string }> {
  return messages
    .slice(-10)
    .map((m) => ({ role: m.role, content: typeof m.content === 'string' ? m.content : '' }));
}

@Injectable()
export class AssistantEffects {
  sendQuery$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssistantActions.sendQuery),
      withLatestFrom(
        this.store.select(selectCurrentUser),
        this.store.select(selectCurrentMatter),
        this.store.select(selectAdminSessionId),
        this.store.select(selectAdminSessionState),
        this.store.select(selectPendingWrite),
        this.store.select(selectAdminHistory),
        this.store.select(selectPendingClarification),
      ),
      switchMap(([{ query, provider, model, conversationId, autoCreateConversation }, user, matter, adminSessionId, adminSessionState, pendingWrite, adminHistory, pendingClarification]) => {
        if (!user?.tenantId) {
          return of(
            AssistantActions.queryFailed({
              error: 'Missing tenant context. Please sign in again.',
            })
          );
        }

        return this.apiService
          .queryAssistant({
            query,
            provider,
            model,
            tenantId: user.tenantId,
            matterId: matter?._id,
            conversationId,
            autoCreateConversation,
            stream: false,
            sessionId: adminSessionId ?? undefined,
            activeMatterId: adminSessionState?.activeMatterId ?? matter?._id,
            activeClientId: adminSessionState?.activeClientId,
            history: adminHistory.length ? adminHistory : [],
            pendingWrite: pendingWrite ?? null,
            pendingClarification: pendingClarification ?? null,
          })
          .pipe(
            map((response) => AssistantActions.receiveResponse({ response })),
            catchError((error) =>
              of(
                AssistantActions.queryFailed({
                  error: error?.error?.message || 'Assistant query failed',
                })
              )
            )
          );
      })
    )
  );

  confirmPendingWrite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssistantActions.confirmPendingWrite),
      withLatestFrom(
        this.store.select(selectCurrentUser),
        this.store.select(selectCurrentMatter),
        this.store.select(selectAdminSessionState),
      ),
      switchMap(([{ pendingWrite, sessionId }, user, matter, adminSessionState]) => {
        if (!user?.tenantId) {
          return of(AssistantActions.queryFailed({ error: 'Missing tenant context.' }));
        }

        return this.apiService
          .queryAssistant({
            query: 'confirmed',
            tenantId: user.tenantId,
            matterId: matter?._id,
            sessionId,
            activeMatterId: adminSessionState?.activeMatterId ?? matter?._id,
            activeClientId: adminSessionState?.activeClientId,
            history: [],
            pendingWrite,
            confirmed: true,
          })
          .pipe(
            switchMap((response) => {
              const actions: Action[] = [AssistantActions.receiveResponse({ response })];
              // If a document was just generated, refresh the matter's document list
              if (pendingWrite.tool === 'document' && !response.pendingWrite) {
                const matterId = adminSessionState?.activeMatterId ?? matter?._id;
                if (matterId) {
                  actions.push(CrmActions.loadMatterDocuments({ matterId }));
                }
              }
              return of(...actions);
            }),
            catchError((error) =>
              of(AssistantActions.queryFailed({ error: error?.error?.message || 'Confirmation failed' }))
            )
          );
      })
    )
  );

  cancelPendingWrite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssistantActions.cancelPendingWrite),
      withLatestFrom(
        this.store.select(selectCurrentUser),
        this.store.select(selectCurrentMatter),
        this.store.select(selectAdminSessionState),
      ),
      switchMap(([{ pendingWrite, sessionId }, user, matter, adminSessionState]) => {
        if (!user?.tenantId) {
          return of(AssistantActions.queryFailed({ error: 'Missing tenant context.' }));
        }

        return this.apiService
          .queryAssistant({
            query: 'cancel',
            tenantId: user.tenantId,
            matterId: matter?._id,
            sessionId,
            activeMatterId: adminSessionState?.activeMatterId ?? matter?._id,
            activeClientId: adminSessionState?.activeClientId,
            history: [],
            pendingWrite,
            confirmed: false,
          })
          .pipe(
            map((response) => AssistantActions.receiveResponse({ response })),
            catchError((error) =>
              of(AssistantActions.queryFailed({ error: error?.error?.message || 'Cancellation failed' }))
            )
          );
      })
    )
  );

  loadConversations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssistantActions.loadConversations),
      switchMap(({ matterId, clientId, includeArchived }) =>
        this.apiService.listConversations(matterId, clientId, includeArchived).pipe(
          map((conversations) => {
            const normalized = (conversations || []).map((conv: any) => ({
              ...conv,
              messageCount:
                typeof conv?.messageCount === 'number'
                  ? conv.messageCount
                  : Array.isArray(conv?.messages)
                    ? conv.messages.length
                    : 0,
            }));

            return AssistantActions.loadConversationsSuccess({ conversations: normalized });
          }),
          catchError((error) =>
            of(
              AssistantActions.loadConversationsFailure({
                error: error?.error?.message || 'Failed to load conversations',
              })
            )
          )
        )
      )
    )
  );

  selectConversation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssistantActions.selectConversation),
      map(({ conversationId }) =>
        AssistantActions.loadConversationMessages({ conversationId })
      )
    )
  );

  loadConversationMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssistantActions.loadConversationMessages),
      switchMap(({ conversationId }) =>
        this.apiService.getConversation(conversationId).pipe(
          map((conversation) =>
            AssistantActions.loadConversationMessagesSuccess({ conversation })
          ),
          catchError((error) =>
            of(
              AssistantActions.loadConversationMessagesFailure({
                error: error?.error?.message || 'Failed to load conversation',
              })
            )
          )
        )
      )
    )
  );

  newConversation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssistantActions.newConversation),
      switchMap(({ matterId, clientId, title }) =>
        this.apiService.createConversation(matterId, clientId, title).pipe(
          map((conversation) =>
            AssistantActions.newConversationSuccess({ conversation })
          ),
          catchError((error) =>
            of(
              AssistantActions.newConversationFailure({
                error: error?.error?.message || 'Failed to create conversation',
              })
            )
          )
        )
      )
    )
  );

  updateConversation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssistantActions.updateConversation),
      switchMap(({ conversationId, updates }) =>
        this.apiService.updateConversation(conversationId, updates).pipe(
          map((conversation) =>
            AssistantActions.updateConversationSuccess({ conversation })
          ),
          catchError((error) =>
            of(
              AssistantActions.updateConversationFailure({
                error: error?.error?.message || 'Failed to update conversation',
              })
            )
          )
        )
      )
    )
  );

  archiveConversation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssistantActions.archiveConversation),
      switchMap(({ conversationId }) =>
        this.apiService.archiveConversation(conversationId).pipe(
          map(() =>
            AssistantActions.archiveConversationSuccess({ conversationId })
          ),
          catchError((error) =>
            of(
              AssistantActions.loadConversationsFailure({
                error: error?.error?.message || 'Failed to archive conversation',
              })
            )
          )
        )
      )
    )
  );

  deleteConversation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssistantActions.deleteConversation),
      switchMap(({ conversationId }) =>
        this.apiService.deleteConversation(conversationId).pipe(
          map(() =>
            AssistantActions.deleteConversationSuccess({ conversationId })
          ),
          catchError((error) =>
            of(
              AssistantActions.deleteConversationFailure({
                error: error?.error?.message || 'Failed to delete conversation',
              })
            )
          )
        )
      )
    )
  );

  refreshConversations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AssistantActions.sendQuery,
        AssistantActions.receiveResponse,
        AssistantActions.newConversationSuccess,
        AssistantActions.updateConversationSuccess,
        AssistantActions.archiveConversationSuccess,
        AssistantActions.deleteConversationSuccess,
      ),
      withLatestFrom(this.store.select(selectCurrentMatter)),
      map(([_, matter]) =>
        AssistantActions.loadConversations({
          matterId: matter?._id,
          includeArchived: false,
        })
      ),
    )
  );

  initializeConversationAfterLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AssistantActions.loadConversationsSuccess),
      withLatestFrom(
        this.store.select(selectCurrentMatter),
        this.store.select(selectCurrentConversationId),
      ),
      map(([_, matter, currentConversationId]) => {
        if (!matter?._id || currentConversationId) {
          return null;
        }

        return AssistantActions.newConversation({
          title: 'New Conversation',
          matterId: matter._id,
        });
      }),
      filter((action): action is ReturnType<typeof AssistantActions.newConversation> => Boolean(action)),
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private apiService: ApiService
  ) {}
}
